import { Observable, Subject } from "rxjs";
import { filter, map } from "rxjs/operators";
import { IOrderApprovalResponse } from "../interfaces/IOrderApprovalResponse";
import { IApproveRejectRequest } from "./../interfaces/IApproveRejectRequest";
import { IOrderApproval } from "./../interfaces/IOrderApproval";
import { IOrdersAwaitingApproval } from "./../interfaces/IOrdersAwaitingApproval";
import { ApiEndpointResolver, IsEcom } from "./apiEndpointResolver";
import { CartService } from "./cartService";
import EnvironmentService from "./environmentService";
import HttpClient from "./httpClient";

const orderApprovalSubject = new Subject<IOrderApproval>();
const getMoreOrdersSubject = new Subject<void>();
const reduceOrderApprovalCountSubject = new Subject<IOrderApproval>();
const approvalErrorsSubject = new Subject<any>();
const completeSubject = new Subject<any>();

const ordersCompleted: Record<string, number> = {};
const ordersApproveReject: Record<string, number> = {};
const approvalErrors: Record<string, IOrderApprovalResponse> = {};

const OrderApprovalService = {
  emit: (data: IOrderApproval) => {
    orderApprovalSubject.next(data);
  },
  getOrderApprovalObservable: () => {
    return orderApprovalSubject.asObservable();
  },
  emitGetMoreOrders: () => {
    getMoreOrdersSubject.next();
  },
  getMoreOrdersObservable: () => {
    return getMoreOrdersSubject.asObservable();
  },
  getReduceOrderApprovalCountObservable: () => {
    return reduceOrderApprovalCountSubject.asObservable();
  },
  completeOrder: (id: string, order: IOrderApproval) => {
    ordersCompleted[id] = 1;
    completeSubject.next({ approvalGuid: id });
    reduceOrderApprovalCountSubject.next(order);
  },
  isCompleted: (id: string) => {
    return ordersCompleted[id] === 1;
  },
  orderApproveReject: (id: string) => {
    ordersApproveReject[id] = 1;
  },
  setApproveRejectOrdersComplete: () => {
    Object.keys(ordersApproveReject).forEach((key) => {
      ordersCompleted[key] = 1;
    });
  },
  emitApprovalError: (
    approvalGuid: string,
    response: IOrderApprovalResponse
  ) => {
    approvalErrors[approvalGuid] = response;
    approvalErrorsSubject.next({
      approvalGuid: approvalGuid,
      response: response,
    });
  },
  getApprovalErrorsObservable: (approvalGuid: string) => {
    return approvalErrorsSubject.asObservable().pipe(
      filter((a) => {
        return a.approvalGuid === approvalGuid;
      })
    );
  },
  getCompleteOrderObservable: (approvalGuid: string) => {
    return completeSubject.asObservable().pipe(
      filter((a) => {
        return a.approvalGuid === approvalGuid;
      })
    );
  },
  getApprovalError: (approvalGuid: string) => {
    return approvalErrors[approvalGuid];
  },
  approveRejectOrder(
    order: IOrderApproval
  ): Observable<IOrderApprovalResponse> {
    if (IsEcom() || !EnvironmentService.get().approveRejectOrderUrl) {
      return HttpClient.post({
        baseUri: ApiEndpointResolver.approveRejectOrderUrl(order),
        body: order,
      });
    } else {
      const approveRejectRequest: IApproveRejectRequest = {
        approve: order.checkOutRequestType === 0 ? true : false,
        message: "",
        checkoutRequestType: order.checkOutRequestType,
      };
      return HttpClient.post({
        baseUri: ApiEndpointResolver.approveRejectOrderUrl(order),
        body: approveRejectRequest,
      });
    }
  },
  getOrderApprovals(): Observable<IOrdersAwaitingApproval> {
    return HttpClient.get({
      baseUri: ApiEndpointResolver.getOrderApprovalsUrl(),
    });
  },
  saveWorkflow(): Observable<void> {
    const observable = HttpClient.post(
      {
        baseUri: ApiEndpointResolver.saveWorkflowUrl(),
      },
      {
        withCredentials: true,
      }
    );

    return observable.pipe(
      map((r) => {
        CartService.setCartChangeCookie();
        return;
      })
    );
  },
};

export default OrderApprovalService;
